import React, {useState, useEffect, useMemo, useCallback} from 'react'
import {useIntl} from 'react-intl'
import {KTCard, KTCardBody, Table, Loading} from '../../../components'
import {CustomCellDouble} from '../../../components/Table/columns/CustomCellDouble'
import {CustomCell} from '../../../components/Table/columns/CustomCell'
import {CustomHeader} from '../../../components/Table/columns/CustomHeader'
import {ListHeader} from '../../../components/Table/header/ListHeader'
import {ListLoading} from '../../../components/Table/ListLoading'
import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {
  paymentPlanExpenseInvoiceGetAll,
  paymentPlanExpenseInvoiceExcel,
} from '../../../redux/slices/paymentplan.slice'
import {projectLookup, allCompanyLookup, basicLookup} from '../../../redux/slices/lookup.slice'
import {useMemoizedHandlers, useMemoizedStaticData} from '../../../utils/performanceOptimizations'

const selectExpenseData = (state) => ({
  expenseinvoice: state.paymentplan.expenseinvoice,
  loading: state.paymentplan.loading
})

const selectLookupData = (state) => ({
  projectLookupList: state.lookup.projectLookupList,
  companyLookup: state.lookup.companyLookup,
  PaymentPlanType: state.lookup.PaymentPlanType
})

function List() {
  const intl = useIntl()
  const {expenseinvoice, loading} = useSelector(selectExpenseData)
  const {projectLookupList, companyLookup, PaymentPlanType} = useSelector(selectLookupData)
  const dispatch = useDispatch()
  const pageKey = 'EXPENSE'

  const [params, setParams] = useState()

  useEffect(() => {
    if (params) dispatch(paymentPlanExpenseInvoiceGetAll(params))
  }, [dispatch, params])

  useEffect(() => {
    dispatch(projectLookup())
    dispatch(allCompanyLookup())
    dispatch(basicLookup('PaymentPlanType'))
  }, [dispatch])

  const handlers = useMemoizedHandlers({
    handleSorting: (item) => {
      setParams((prevState) => ({...prevState, sort: item && item.sort, order: item && item.order}))
    },
    handleSearch: (item) => {
      setParams((prevState) => ({...prevState, name: item}))
    },
    handlePagination: (pagination) => {
      setParams((prevState) => ({...prevState, ...pagination}))
    },
    handleFilterSubmit: (values) => {
      setParams({...values})
    }
  }, [])

  const handleExcelExport = useCallback(() => {
    if (params) {
      dispatch(
        paymentPlanExpenseInvoiceExcel({
          params,
          fileName: intl.formatMessage({id: 'MENU.CASH.EXPENSE'}),
        })
      )
    }
  }, [dispatch, params, intl])

  const columns = useMemo(() => [
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handlers.handleSorting}
          title={intl.formatMessage({id: 'PAYMENTPLAN.GET.NAME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'name',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handlers.handleSorting}
          title={intl.formatMessage({id: 'PAYMENTPLAN.GET.PROJECTNAME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'projectName',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handlers.handleSorting}
          title={intl.formatMessage({id: 'PAYMENTPLAN.GET.COMPANYNAME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'companyName',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handlers.handleSorting}
          title={intl.formatMessage({id: 'PAYMENTPLAN.GET.TOTALPRICE'})}
          className='min-w-125px'
        />
      ),
      accessor: 'totalPrice',
      Cell: ({...props}) => <CustomCell item={props} price />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handlers.handleSorting}
          title={intl.formatMessage({id: 'PAYMENTPLAN.GET.DEFERMENTDATE'})}
          className='min-w-125px'
        />
      ),
      accessor: 'defermentDate',
      Cell: ({...props}) => <CustomCell item={props} date='short' />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handlers.handleSorting}
          title={intl.formatMessage({id: 'PAYMENTPLAN.GET.PAYMENTPLANTYPENAME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'paymentPlanTypeName',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handlers.handleSorting}
          title={intl.formatMessage({id: 'PAYMENTPLAN.GET.DESCRIPTION'})}
          className='min-w-125px'
        />
      ),
      accessor: 'description',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handlers.handleSorting}
          title={intl.formatMessage({id: 'LIST.LASTMODIFIEDINFO'})}
          className='min-w-125px'
        />
      ),
      accessor: 'lastModifiedBy',
      Cell: ({...props}) => <CustomCellDouble item={props} double='lastModifiedDate' />,
    },
  ], [intl, handlers.handleSorting])

  const actionButtons = useMemoizedStaticData(() => [
    {
      title: 'BUTTON.EXCEL',
      onClick: handleExcelExport,
      icon: '/images/icons/duotune/arrows/arr044.svg',
      variant: 'light-primary',
    },
    {
      title: 'FILTER',
      icon: '/images/icons/duotune/general/gen031.svg',
      variant: 'light-primary',
      list: [
        {
          title: 'PAYMENTPLAN.GET.PROJECTNAME',
          data: projectLookupList,
          name: 'projectId',
        },
        {
          title: 'PAYMENTPLAN.GET.COMPANYNAME',
          data: companyLookup,
          name: 'companyId',
        },
        {
          title: 'PAYMENTPLAN.GET.PAYMENTPLANTYPENAME',
          data: PaymentPlanType,
          name: 'paymentPlanTypeId',
        },
        {
          title: 'PAYMENTPLAN.GET.STARTDATE',
          type: 'date',
          name: 'startDate',
        },
        {
          title: 'PAYMENTPLAN.GET.ENDDATE',
          type: 'date',
          name: 'endDate',
        },
      ],
    },
  ], [projectLookupList, companyLookup, PaymentPlanType, handleExcelExport])

  return (
    <>
      <PageTitle>{intl.formatMessage({id: 'MENU.CASH.EXPENSE'})}</PageTitle>
      <KTCard>
        <ListHeader
          search={handlers.handleSearch}
          actionButtons={actionButtons}
          onSubmit={handlers.handleFilterSubmit}
          pages={pageKey}
        />
        <KTCardBody>
          {!expenseinvoice && <ListLoading />}
          {expenseinvoice && (
            <Table
              items={expenseinvoice}
              cols={columns}
              handlePagination={handlers.handlePagination}
              paddless
              pages={pageKey}
            />
          )}
        </KTCardBody>
      </KTCard>

      {loading && <Loading />}
    </>
  )
}
export {List}
